import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Label,
} from "@exhousing/design-system";
import { CalendarClock, Code2, Eye, Pencil, Plus, Send, X } from "lucide-react";
import { useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";

import { RichTextEditor } from "@/components/RichTextEditor";
import {
  useCreateMailingJob,
  useMailingJobs,
  useMailingTemplates,
  usePreviewMailingJob,
  useRunMailingJob,
  useUpdateMailingJob,
} from "@/hooks/api/useMailing";
import type { components } from "@/types/api";

type Job = components["schemas"]["MailingJobResponse"];

const STATUS_TONE: Record<string, "neutral" | "success" | "warning" | "danger"> = {
  pending: "warning",
  scheduled: "neutral",
  running: "neutral",
  completed: "success",
  failed: "danger",
};

function fmtWhen(iso: string | null | undefined): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ISO → value for <input type="datetime-local"> (local time, no seconds/zone).
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

export function MailingPage() {
  const { data: jobs } = useMailingJobs();
  const { data: templates } = useMailingTemplates();
  const create = useCreateMailingJob();
  const update = useUpdateMailingJob();
  const run = useRunMailingJob();
  const preview = usePreviewMailingJob();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [templateId, setTemplateId] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("subscribers");
  const [scheduledAt, setScheduledAt] = useState("");
  const [segmentCity, setSegmentCity] = useState("");
  const [showSource, setShowSource] = useState(false);
  const [previewState, setPreviewState] = useState<{ html: string; count: number } | null>(
    null,
  );

  const selectClass =
    "flex h-11 w-full rounded-md border border-input bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setTemplateId("");
    setSubject("");
    setBody("");
    setAudience("subscribers");
    setScheduledAt("");
    setSegmentCity("");
  };

  const applyTemplate = (id: string) => {
    setTemplateId(id);
    const t = (templates ?? []).find((x) => x.id === id);
    if (t) {
      setSubject(t.subject);
      setBody(t.body_html);
    }
  };

  const startNew = () => {
    resetForm();
    setShowForm(true);
  };

  const startEdit = (j: Job) => {
    setEditingId(j.id);
    setTemplateId(j.template_id ?? "");
    setSubject(j.subject);
    setBody(j.body_html);
    setAudience(j.audience);
    setSegmentCity((j.segment?.city as string | undefined) ?? "");
    setScheduledAt(j.scheduled_at ? toLocalInput(j.scheduled_at) : "");
    setShowForm(true);
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject) return;
    const payload = {
      subject,
      body_html: body,
      audience,
      template_id: templateId || undefined,
      scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : undefined,
      segment: segmentCity ? { city: segmentCity } : undefined,
    };
    if (editingId) {
      update.mutate(
        { id: editingId, body: payload },
        {
          onSuccess: () => {
            toast.success("Mailing updated");
            resetForm();
          },
          onError: (err) => toast.error(err.message),
        },
      );
    } else {
      create.mutate(payload, {
        onSuccess: (job) => {
          toast.success(job.status === "scheduled" ? "Mailing scheduled" : "Mailing created");
          resetForm();
        },
        onError: (err) => toast.error(err.message),
      });
    }
  };

  const openPreview = (id: string) =>
    preview.mutate(id, {
      onSuccess: (r) => setPreviewState({ html: r.html, count: r.recipient_count }),
      onError: () => toast.error("Could not render preview"),
    });

  const busy = create.isPending || update.isPending;

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight">Mailings</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {jobs ? `${jobs.length} campaigns` : "Loading…"}
          </p>
        </div>
        <Button onClick={startNew}>
          <Plus className="size-4" />
          New mailing
        </Button>
      </header>

      {showForm && (
        <Card className="animate-fade-in-up">
          <CardContent className="space-y-4 pt-6">
            <p className="text-sm font-medium">
              {editingId ? "Edit mailing" : "New mailing"}
            </p>
            <form onSubmit={onSubmit} className="space-y-4">
              {templates && templates.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="template">Start from a template</Label>
                  <select
                    id="template"
                    value={templateId}
                    onChange={(e) => applyTemplate(e.target.value)}
                    className={selectClass}
                  >
                    <option value="">— None (blank) —</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="subject">Subject</Label>
                  <Input
                    id="subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="audience">Audience</Label>
                  <select
                    id="audience"
                    value={audience}
                    onChange={(e) => setAudience(e.target.value)}
                    className={selectClass}
                  >
                    <option value="subscribers">Confirmed subscribers</option>
                    <option value="tenants">Tenant subscribers</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="segment-city">Only city (optional)</Label>
                  <Input
                    id="segment-city"
                    placeholder="e.g. Amsterdam"
                    value={segmentCity}
                    onChange={(e) => setSegmentCity(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="scheduled">Schedule (optional)</Label>
                  <Input
                    id="scheduled"
                    type="datetime-local"
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="body">Message</Label>
                  <button
                    type="button"
                    onClick={() => setShowSource((s) => !s)}
                    className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                  >
                    <Code2 className="size-3.5" />
                    {showSource ? "Rich text" : "HTML source"}
                  </button>
                </div>
                {showSource ? (
                  <textarea
                    id="body"
                    rows={8}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    className="min-h-40 w-full resize-y rounded-md border border-input bg-card p-3 font-mono text-xs leading-relaxed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                ) : (
                  <RichTextEditor value={body} onChange={setBody} />
                )}
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={busy}>
                  {editingId
                    ? "Save changes"
                    : scheduledAt
                      ? "Schedule mailing"
                      : "Create mailing"}
                </Button>
                <Button type="button" variant="ghost" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {jobs && jobs.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-6 py-3 font-medium">Subject</th>
                  <th className="px-6 py-3 font-medium">Audience</th>
                  <th className="px-6 py-3 font-medium tabular-nums">Progress</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                  <th className="px-6 py-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => {
                  const editable = j.status === "pending" || j.status === "scheduled";
                  return (
                    <tr key={j.id} className="border-b border-border/60 last:border-0">
                      <td className="px-6 py-3 font-medium">{j.subject}</td>
                      <td className="px-6 py-3 capitalize text-muted-foreground">
                        {j.audience}
                      </td>
                      <td className="px-6 py-3 tabular-nums">
                        {j.sent}/{j.total}
                        {j.failed ? ` (${j.failed} failed)` : ""}
                      </td>
                      <td className="px-6 py-3">
                        <Badge tone={STATUS_TONE[j.status] ?? "neutral"} dot>
                          {j.status}
                        </Badge>
                        {j.status === "scheduled" && j.scheduled_at && (
                          <span className="ml-2 inline-flex items-center gap-1 text-xs text-muted-foreground">
                            <CalendarClock className="size-3.5" />
                            {fmtWhen(j.scheduled_at)}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-3">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={preview.isPending}
                            onClick={() => openPreview(j.id)}
                          >
                            <Eye className="size-4" />
                            Preview
                          </Button>
                          {editable && (
                            <Button size="sm" variant="ghost" onClick={() => startEdit(j)}>
                              <Pencil className="size-4" />
                              Edit
                            </Button>
                          )}
                          {j.status === "pending" && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={run.isPending}
                              onClick={() =>
                                run.mutate(j.id, {
                                  onSuccess: (r) =>
                                    toast.success(`Sent to ${r.sent} recipient(s)`),
                                  onError: () => toast.error("Send failed"),
                                })
                              }
                            >
                              <Send className="size-4" />
                              Send
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="flex flex-col items-center gap-3 py-16 text-center text-muted-foreground">
              <Send className="size-8 opacity-40" />
              <p className="text-sm">No mailings yet.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {previewState &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
            onMouseDown={() => setPreviewState(null)}
          >
          <div
            className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-border bg-card shadow-xl"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-border px-5 py-3">
              <div>
                <p className="font-medium">Email preview</p>
                <p className="text-xs text-muted-foreground">
                  Would send to {previewState.count} recipient
                  {previewState.count === 1 ? "" : "s"}
                </p>
              </div>
              <button
                onClick={() => setPreviewState(null)}
                aria-label="Close"
                className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="size-5" />
              </button>
            </div>
            <iframe
              title="Email preview"
              sandbox=""
              srcDoc={previewState.html}
              className="h-[60vh] w-full bg-white"
            />
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
